'use client';
import React from 'react';
import { MemberProfile } from '@/lib/supabase/types';
import ProfileCard from './ProfileCard';
import { SortOption } from './DiscoverScreen';
import { Users, ChevronLeft, ChevronRight, Crown, Lock } from 'lucide-react';

interface DiscoverGridProps {
  profiles: MemberProfile[];
  allFiltered: MemberProfile[];
  sort: SortOption;
  onSortChange: (s: SortOption) => void;
  likedIds: Set<string>;
  onLike: (id: string) => void;
  onPass: (id: string) => void;
  onViewProfile: (p: MemberProfile) => void;
  currentPage: number;
  totalPages: number;
  totalResults: number;
  onPageChange: (p: number) => void;
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  isPhotoBlocked?: boolean;
  photoStatus?: string;
  isPremium?: boolean;
  previewIds?: Set<string>;
}

// Premium-only sort options
const PREMIUM_SORT_OPTIONS: SortOption[] = ['newest', 'online'];

const sortOptions: { value: SortOption; label: string; premiumOnly?: boolean }[] = [
  { value: 'newest', label: 'Newest Members', premiumOnly: true },
  { value: 'online', label: 'Online Now', premiumOnly: true },
  { value: 'complete', label: 'Profile Complete' },
  { value: 'age-asc', label: 'Age: Youngest' },
  { value: 'age-desc', label: 'Age: Oldest' },
];

export default function DiscoverGrid({
  profiles,
  sort,
  onSortChange,
  likedIds,
  onLike,
  onPass,
  onViewProfile,
  currentPage,
  totalPages,
  totalResults,
  onPageChange,
  searchQuery,
  onSearchChange,
  isPhotoBlocked,
  photoStatus,
  isPremium,
  previewIds,
}: DiscoverGridProps) {
  return (
    <div className="space-y-5">
      {/* Sort + Search bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="hidden lg:flex items-center gap-2 flex-1">
          <input
            type="text"
            placeholder="Search profiles..."
            className="input-jsn flex-1 py-2 text-sm max-w-xs"
            value={searchQuery || ''}
            onChange={e => onSearchChange?.(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground font-medium">Sort:</span>
          {sortOptions.map(opt => {
            const isLocked = opt.premiumOnly && !isPremium;
            const isActive = sort === opt.value;
            const isProfileComplete = opt.value === 'complete';
            return (
              <button
                key={opt.value}
                onClick={() => onSortChange(opt.value)}
                className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                  isActive && isProfileComplete
                    ? 'text-white'
                    : isActive
                    ? 'bg-primary text-white'
                    : isLocked
                    ? 'bg-muted/20 border border-dashed border-border text-muted-foreground/60 cursor-not-allowed'
                    : 'bg-muted/20 border border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
                }`}
                style={isActive && isProfileComplete ? { backgroundColor: '#008000' } : {}}
              >
                {isLocked && <Lock size={10} />}
                {opt.label}
                {opt.premiumOnly && !isLocked && <Crown size={10} className="text-primary" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {totalResults > 0 ? (
            <><span className="font-semibold text-foreground">{totalResults}</span> profiles found</>
          ) : (
            'No profiles match your filters'
          )}
        </p>
        {totalPages > 1 && (
          <p className="text-xs text-muted-foreground">
            Page {currentPage} of {totalPages}
          </p>
        )}
      </div>

      {/* Grid */}
      {profiles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 space-y-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-muted/30 flex items-center justify-center">
            <Users size={28} className="text-muted-foreground" />
          </div>
          <div>
            <p className="font-semibold text-foreground">No profiles found</p>
            <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters or search query.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {profiles.map(profile => (
            <ProfileCard
              key={profile.id}
              profile={profile}
              isLiked={likedIds.has(profile.id)}
              onLike={() => onLike(profile.id)}
              onPass={() => onPass(profile.id)}
              onView={() => onViewProfile(profile)}
              isPhotoBlocked={isPhotoBlocked}
              photoStatus={photoStatus}
              isPreview={previewIds?.has(profile.id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const page = i + 1;
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`w-9 h-9 rounded-lg text-sm font-semibold transition-colors ${
                  currentPage === page
                    ? 'bg-primary text-white' :'border border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
                }`}
              >
                {page}
              </button>
            );
          })}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}